import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getKumesKartiBilgileri,
  getKumesKartiHindiBilgileri,
  getKumesler,
  getDonemler,
} from "../services/api";
import { AuthContext } from "../context/AuthContext";

const ORANGE = "#FF7A2B";
const BG = "#EEF2F7";

export default function KumesKartiPage() {
  const navigate = useNavigate();
  const { donemId: urlDonemId } = useParams();
  const { state } = useLocation();
  const printRef = useRef();
  const { token } = useContext(AuthContext);

  // Kümes ve dönem state'leri
  const [kumesler, setKumesler] = useState([]);
  const [selectedKumesId, setSelectedKumesId] = useState("");
  const [donemler, setDonemler] = useState([]);
  const [selectedDonemId, setSelectedDonemId] = useState(urlDonemId || "");
  const [selectedDonem, setSelectedDonem] = useState(null);
  const [kumesData, setKumesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kumesLoading, setKumesLoading] = useState(true);
  const [donemLoading, setDonemLoading] = useState(false);
  const [error, setError] = useState("");

  // Kümeleri yükle
  useEffect(() => {
    const fetchKumesler = async () => {
      try {
        setKumesLoading(true);
        const res = await getKumesler();
        const list = res?.data?.data || [];
        setKumesler(list);

        // State'den gelen kumesId varsa kümes seç (DonemDetayPage'den geliyor)
        const targetKumesId = state?.kumesId || state?.initialData?.KUMES_ID;
        if (targetKumesId) {
          setSelectedKumesId(String(targetKumesId));
        }
      } catch (err) {
        setError("Kümesler yüklenemedi.");
      } finally {
        setKumesLoading(false);
      }
    };

    fetchKumesler();
  }, [state?.kumesId, state?.initialData?.KUMES_ID]);

  // Kümes seçildiğinde dönemleri yükle
  useEffect(() => {
    if (!selectedKumesId) {
      setDonemler([]);
      setSelectedDonemId("");
      setSelectedDonem(null);
      return;
    }

    const fetchDonemler = async () => {
      try {
        setDonemLoading(true);
        const res = await getDonemler(selectedKumesId);
        const list = res?.data?.data || [];
        setDonemler(list);

        // URL'den veya state'den gelen donemId varsa seç
        const targetDonemId = urlDonemId || state?.donemId || state?.initialData?.DONEM_ID;
        if (targetDonemId) {
          const found = list.find((d) => String(d.DONEM_ID) === String(targetDonemId));
          if (found) {
            setSelectedDonem(found);
            setSelectedDonemId(String(targetDonemId));
          }
        }
      } catch (err) {
        setError("Dönemler yüklenemedi.");
      } finally {
        setDonemLoading(false);
      }
    };

    fetchDonemler();
  }, [selectedKumesId, urlDonemId, state?.donemId, state?.initialData?.DONEM_ID]);

  // Seçili dönemin kartını yükle
  useEffect(() => {
    if (!selectedDonemId || !token) {
      setKumesData([]);
      return;
    }

    const fetchKumesKarti = async () => {
      setLoading(true);
      setError("");
      try {
        const hayvanCinsId = selectedDonem?.HAYVAN_CINS_ID || state?.hayvanCinsId || 1;
        const fetcher =
          hayvanCinsId === 2 ? getKumesKartiHindiBilgileri : getKumesKartiBilgileri;

        const response = await fetcher(selectedDonemId);
        setKumesData(response?.data?.data || []);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Kümes kartı yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };

    fetchKumesKarti();
  }, [selectedDonemId, token, selectedDonem?.HAYVAN_CINS_ID, state?.hayvanCinsId]);

  const handleKumesChange = (e) => {
    const id = e.target.value;
    setSelectedKumesId(id);
    setSelectedDonemId("");
    setSelectedDonem(null);
    setKumesData([]);
  };

  const handleDonemChange = (e) => {
    const id = e.target.value;
    setSelectedDonemId(id);
    const found = donemler.find((d) => String(d.DONEM_ID) === String(id));
    setSelectedDonem(found || null);
  };

const selectedKumesAdi =
  selectedDonem?.KUMES_ADI ||
  kumesler.find((k) => String(k.KUMES_ID) === String(selectedKumesId))?.KUMES_ADI ||
  state?.initialData?.KUMES_ADI ||
  "-";

const buildPrintRows = () => {
  return kumesData
    .map(
      (record) => `
        <tr class="${record.AKTIF_GUN_MU === "E" ? "aktif-gun" : ""}">
          <td>${formatNumber(record.GUN)}</td>
          <td>${record.TARIH_STR || "-"}</td>
          <td>${formatNumber(record.CANLI_HAYVAN_ADET)}</td>
          <td>${formatNumber(record.GUNLUK_OLUM)}</td>
          <td>
            <div class="main-value">${formatNumber(record.ZIYARET_CANLI_AGIRLIK)} kg</div>
            <div class="sub-value">Hedef: ${formatNumber(record.CANLI_AGIRLIK)} kg</div>
          </td>
          <td>
            <div class="main-value">${formatNumber(record.HESAP_GUNLUK_YEM_TUKETIM)} kg</div>
            <div class="sub-value">Hedef: ${formatNumber(record.GUNLUK_YEM_TUKETIM)} kg</div>
          </td>
          <td>
            <div class="main-value">${formatNumber(record.HESAP_GUNLUK_SU_TUKETIM)} lt</div>
            <div class="sub-value">Hedef: ${formatNumber(record.GUNLUK_SU_TUKETIM)} lt</div>
          </td>
          <td>
            <div class="main-value">${formatNumber(record.GUNLUK_ISI)} °C</div>
            <div class="sub-value">Hedef: ${formatNumber(record.HESAP_GUNLUK_ISI)} °C</div>
          </td>
        </tr>
      `
    )
    .join("");
};


  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("about:blank", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>eKümes</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  margin: 0;
  padding: 24px 0;
  background: #fff;
}

.print-page {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px 24px;
}
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #FF7A2B; padding-bottom: 15px; }
          .header h1 { color: #FF7A2B; font-size: 24px; margin-bottom: 8px; }
          .header p { color: #666; font-size: 12px; }
          .info-row { display: flex; gap: 20px; margin-bottom: 15px; flex-wrap: wrap; }
          .info-item { background: #f5f5f5; padding: 8px 15px; border-radius: 6px; font-size: 12px; }
          .info-item strong { color: #FF7A2B; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #FF7A2B; color: white; padding: 10px 6px; text-align: center; font-weight: 600; }
          td { padding: 8px 6px; text-align: center; border-bottom: 1px solid #e0e0e0; }
          tr:nth-child(even) { background: #fafafa; }
          tr.aktif-gun { background: #fff3e0 !important; font-weight: 600; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; }
          .comparison { font-size: 10px; }
          .target { color: #10B981; font-size: 9px; }
          @media print {
            body { padding: 10px; }
            table { font-size: 9px; }
          }
        </style>
      </head>
      <body>
       <div class="print-page">
        <div class="header">
          <h1>Kümes Kartı</h1>
        </div>
        <div class="info-row">
          <div class="info-item"><strong>Kümes:</strong> ${selectedKumesAdi || "-"}</div>
          <div class="info-item"><strong>Dönem:</strong> ${selectedDonem?.DONEM_NO || "-"}</div>
          <div class="info-item"><strong>Yazdırma:</strong> ${new Date().toLocaleDateString("tr-TR")}</div>
        </div>
        ${printContent.innerHTML}
        <div class="footer">
          Bu belge E-Kümes sistemi tarafından oluşturulmuştur. • ${new Date().toLocaleString("tr-TR")}
        </div>
         </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

const formatNumber = (num) => {
  if (num == null || num === "" || isNaN(num)) return "-";
  return String(num).replace(".", ",");
};

const renderComparison = (actual, expected, unit = "") => {
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
        {formatNumber(actual)} {unit}
      </Typography>
      <Typography sx={{ color: "#10B981", fontSize: 10 }}>
        Hedef: {formatNumber(expected)} {unit}
      </Typography>
    </Box>
  );
};

  // Özet hesaplamaları
  const summary = React.useMemo(() => {
    if (!kumesData.length) return null;
    const lastDay = kumesData[kumesData.length - 1];
    const totalOlum = kumesData.reduce((sum, r) => sum + (Number(r.GUNLUK_OLUM) || 0), 0);
    const avgAgirlik = kumesData.reduce((sum, r) => sum + (Number(r.CANLI_AGIRLIK) || 0), 0) / kumesData.length;

    return {
      toplamGun: kumesData.length,
      toplamOlum: totalOlum,
      sonCanliSayi: lastDay?.CANLI_HAYVAN_ADET || 0,
      ortAgirlik: avgAgirlik,
      sonAgirlik: lastDay?.CANLI_AGIRLIK || 0,
    };
  }, [kumesData]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh", bgcolor: BG }}>
      {/* HEADER */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(135deg, #FF7A2B 0%, #E85D13 100%)",
          color: "#fff",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: "#fff", bgcolor: "rgba(0,0,0,.15)", "&:hover": { bgcolor: "rgba(0,0,0,.25)" } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, md: 28 } }}>
                🐔 Kümes Kartı
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: 14 }}>
                Dönem bazlı üretim özeti ve yazdırma
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!kumesData.length}
              sx={{
                bgcolor: "rgba(255,255,255,.2)",
                color: "#fff",
                fontWeight: 700,
                "&:hover": { bgcolor: "rgba(255,255,255,.3)" },
              }}
            >
              Yazdır
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* KÜMES VE DÖNEM SEÇİCİ */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          {/* Kümes Seçici */}
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Kümes Seçin</InputLabel>
            <Select
              value={selectedKumesId}
              label="Kümes Seçin"
              onChange={handleKumesChange}
              disabled={kumesLoading}
            >
              {kumesler.map((k) => (
                <MenuItem key={k.KUMES_ID} value={String(k.KUMES_ID)}>
                  {k.KUMES_ADI}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Dönem Seçici */}
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Dönem Seçin</InputLabel>
            <Select
              value={selectedDonemId}
              label="Dönem Seçin"
              onChange={handleDonemChange}
              disabled={donemLoading || !selectedKumesId}
            >
              {donemler.map((d) => (
                <MenuItem key={d.DONEM_ID} value={String(d.DONEM_ID)}>
                  Dönem {d.DONEM_NO} • {d.HAYVAN_CINS_ADI || ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedDonem && (
            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
              <Chip label={`Kümes: ${selectedDonem.KUMES_ADI || kumesler.find(k => String(k.KUMES_ID) === selectedKumesId)?.KUMES_ADI || "-"}`} size="small" sx={{ bgcolor: "#FFF3E0", fontWeight: 600 }} />
              <Chip label={`Dönem: ${selectedDonem.DONEM_NO}`} size="small" sx={{ bgcolor: "#E3F2FD", fontWeight: 600 }} />
            </Stack>
          )}
        </Stack>
      </Paper>



      {/* HATA / LOADING */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading || kumesLoading || donemLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress sx={{ color: ORANGE }} />
        </Box>
      ) : !selectedKumesId ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <Typography sx={{ color: "#6B7280", fontSize: 16 }}>
            Lütfen yukarıdan bir kümes seçin.
          </Typography>
        </Paper>
      ) : !selectedDonemId ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <Typography sx={{ color: "#6B7280", fontSize: 16 }}>
            Lütfen yukarıdan bir dönem seçin.
          </Typography>
        </Paper>
      ) : kumesData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <Typography sx={{ color: "#6B7280" }}>Bu dönem için veri bulunamadı.</Typography>
        </Paper>
      ) : (
        /* TABLO */
        <Paper sx={{ borderRadius: 3, overflow: "hidden" }} ref={printRef}>
          <TableContainer sx={{ maxHeight: "65vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>Gün</TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>Tarih</TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>Canlı Sayı</TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>Günlük Ölüm</TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>
                    Canlı Ağırlık
                    <Typography variant="caption" display="block" sx={{ color: "rgba(255,255,255,.8)" }}>(kg)</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>
                    Yem Tüketimi
                    <Typography variant="caption" display="block" sx={{ color: "rgba(255,255,255,.8)" }}>(kg)</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>
                    Su Tüketimi
                    <Typography variant="caption" display="block" sx={{ color: "rgba(255,255,255,.8)" }}>(lt)</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: ORANGE, color: "#fff", fontWeight: 700 }}>
                    Sıcaklık
                    <Typography variant="caption" display="block" sx={{ color: "rgba(255,255,255,.8)" }}>(°C)</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kumesData.map((record) => (
                  <TableRow
                    key={record.GUN}
                    sx={{
                      bgcolor: record.AKTIF_GUN_MU === "E" ? "#FFF8E1" : "inherit",
                      "&:hover": { bgcolor: "#FFF3E0" },
                    }}
                  >
                    <TableCell align="center">
                      <Chip
                        label={record.GUN}
                        size="small"
                        sx={{
                          bgcolor: record.AKTIF_GUN_MU === "E" ? ORANGE : "#E5E7EB",
                          color: record.AKTIF_GUN_MU === "E" ? "#fff" : "#374151",
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 500 }}>{record.TARIH_STR}</TableCell>
                    <TableCell align="center">{formatNumber(record.CANLI_HAYVAN_ADET)}</TableCell>
                    <TableCell align="center" sx={{ color: "#EF4444", fontWeight: 600 }}>
                      {formatNumber(record.GUNLUK_OLUM)}
                    </TableCell>
                    <TableCell align="center">{renderComparison(record.ZIYARET_CANLI_AGIRLIK,record.CANLI_AGIRLIK,"kg")}</TableCell>
                    <TableCell align="center">
                      {renderComparison(record.HESAP_GUNLUK_YEM_TUKETIM, record.GUNLUK_YEM_TUKETIM, "kg")}
                    </TableCell>
                    <TableCell align="center">
                      {renderComparison(record.HESAP_GUNLUK_SU_TUKETIM, record.GUNLUK_SU_TUKETIM, "lt")}
                    </TableCell>
                    <TableCell align="center">
                      {renderComparison(record.GUNLUK_ISI, record.HESAP_GUNLUK_ISI, "°C")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
